import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, MapPin, Truck, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { money, PICKUP_ADDRESS } from "@/config";

const SHIPPING_FLAT = 350;
const SHIPPING_LOCAL = 60;

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    delivery_method: "envio",
    shipping_zone: "local",
    calle: "", colonia: "", ciudad: "Mérida", cp: "", referencias: "",
    notes: "",
  });
  const idemKey = useRef(crypto.randomUUID());

  useEffect(() => { idemKey.current = crypto.randomUUID(); }, [items]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  useEffect(() => {
    if (items.length === 0) navigate("/tienda");
  }, [items, navigate]);

  const shipping = form.delivery_method === "pickup" ? 0 : form.shipping_zone === "local" ? SHIPPING_LOCAL : SHIPPING_FLAT;
  const total = subtotal + shipping;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validStep1 =
    form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim().length >= 8 &&
    (form.delivery_method === "pickup" || (form.calle.trim() && form.colonia.trim() && form.ciudad.trim() && form.cp.trim()));

  const goToPayment = () => {
    if (!validStep1) {
      toast.error("Revisa tus datos: faltan campos por completar.");
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0 });
  };

  const pay = async () => {
    setLoading(true);
    try {
      const orderPayload = {
        items: items.map((i) => ({ slug: i.slug, variant_id: i.variant_id, qty: i.qty })),
        customer: { name: form.name, email: form.email, phone: form.phone },
        delivery_method: form.delivery_method,
        shipping_zone: form.shipping_zone,
        address:
          form.delivery_method === "envio"
            ? { calle: form.calle, colonia: form.colonia, ciudad: form.ciudad, cp: form.cp, referencias: form.referencias }
            : null,
        notes: form.notes || null,
        idempotency_key: idemKey.current,
      };
      const { data: order } = await api.post("/orders", orderPayload);
      const { data: checkout } = await api.post("/payments/checkout", {
        order_number: order.order_number,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-8" data-testid="checkout-page">
      <h1 className="font-script text-4xl font-semibold text-cocoa">Checkout</h1>

      <div className="mt-8 flex items-center gap-3" aria-label="Pasos del checkout">
        {["Entrega", "Pago"].map((label, i) => (
          <div key={label} className="flex items-center gap-3">
            <span
              className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition-colors ${
                step > i ? "bg-habanero text-white" : "border border-sand bg-white text-clay"
              }`}
              data-testid={`checkout-step-${i + 1}`}
            >
              {i + 1}
            </span>
            <span className={`text-sm font-bold ${step > i ? "text-cocoa" : "text-clay"}`}>{label}</span>
            {i === 0 && <div className="h-px w-10 bg-sand sm:w-20" />}
          </div>
        ))}
      </div>

      <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_380px]">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }}>
          {step === 1 ? (
            <div className="space-y-6" data-testid="checkout-step-shipping">
              <div className="rounded-2xl border border-sand bg-white p-6">
                <h2 className="font-script text-xl font-semibold text-cocoa">Tus datos</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label htmlFor="co-name">Nombre completo</Label>
                    <Input id="co-name" data-testid="checkout-name-input" value={form.name} onChange={set("name")} className="mt-1 border-sand" placeholder="Tu nombre" />
                  </div>
                  <div>
                    <Label htmlFor="co-email">Correo electrónico</Label>
                    <Input id="co-email" type="email" data-testid="checkout-email-input" value={form.email} onChange={set("email")} className="mt-1 border-sand" placeholder="tucorreo@ejemplo.com" />
                  </div>
                  <div>
                    <Label htmlFor="co-phone">Teléfono / WhatsApp</Label>
                    <Input id="co-phone" data-testid="checkout-phone-input" value={form.phone} onChange={set("phone")} className="mt-1 border-sand" placeholder="999 123 4567" />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-sand bg-white p-6">
                <h2 className="font-script text-xl font-semibold text-cocoa">Método de entrega</h2>
                <RadioGroup value={form.delivery_method} onValueChange={(v) => setForm((f) => ({ ...f, delivery_method: v }))} className="mt-4 grid gap-3 sm:grid-cols-2">
                  <div>
                    <RadioGroupItem value="envio" id="dm-envio" className="peer sr-only" data-testid="delivery-envio-radio" />
                    <Label htmlFor="dm-envio" data-testid="delivery-envio-label" className="flex cursor-pointer items-start gap-3 rounded-xl border border-sand p-4 transition-colors peer-data-[state=checked]:border-habanero peer-data-[state=checked]:bg-habanero/5 hover:border-habanero">
                      <Truck size={20} className="mt-0.5 text-leaf" />
                      <div>
                        <p className="font-bold text-cocoa">Envío a domicilio</p>
                        <p className="text-xs text-clay">$60 local · $350 nacional · Lun a jue, 7:00–10:00 p.m.</p>
                      </div>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="pickup" id="dm-pickup" className="peer sr-only" data-testid="delivery-pickup-radio" />
                    <Label htmlFor="dm-pickup" data-testid="delivery-pickup-label" className="flex cursor-pointer items-start gap-3 rounded-xl border border-sand p-4 transition-colors peer-data-[state=checked]:border-habanero peer-data-[state=checked]:bg-habanero/5 hover:border-habanero">
                      <MapPin size={20} className="mt-0.5 text-habanero" />
                      <div>
                        <p className="font-bold text-cocoa">Pick up en Mérida</p>
                        <p className="text-xs text-clay">Sin costo · Con cita previa</p>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>

                {form.delivery_method === "envio" && (
                  <div className="mt-4">
                    <Label>Zona de envío</Label>
                    <RadioGroup
                      value={form.shipping_zone}
                      onValueChange={(v) => setForm((f) => ({ ...f, shipping_zone: v }))}
                      className="mt-2 flex flex-wrap gap-3"
                    >
                      <div>
                        <RadioGroupItem value="local" id="sz-local" className="peer sr-only" data-testid="zone-local-radio" />
                        <Label htmlFor="sz-local" data-testid="zone-local-label" className="flex cursor-pointer flex-col rounded-xl border border-sand px-5 py-3 transition-colors peer-data-[state=checked]:border-habanero peer-data-[state=checked]:bg-habanero/5 hover:border-habanero">
                          <span className="text-sm font-bold text-cocoa">Mérida (local)</span>
                          <span className="text-sm font-bold text-habanero">$60 MXN</span>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem value="nacional" id="sz-nacional" className="peer sr-only" data-testid="zone-nacional-radio" />
                        <Label htmlFor="sz-nacional" data-testid="zone-nacional-label" className="flex cursor-pointer flex-col rounded-xl border border-sand px-5 py-3 transition-colors peer-data-[state=checked]:border-habanero peer-data-[state=checked]:bg-habanero/5 hover:border-habanero">
                          <span className="text-sm font-bold text-cocoa">Resto de México</span>
                          <span className="text-sm font-bold text-habanero">$350 MXN</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
                {form.delivery_method === "envio" ? (
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label htmlFor="co-calle">Calle y número</Label>
                      <Input id="co-calle" data-testid="checkout-street-input" value={form.calle} onChange={set("calle")} className="mt-1 border-sand" placeholder="Calle 21 #123 x 45 y 47" />
                    </div>
                    <div>
                      <Label htmlFor="co-colonia">Colonia</Label>
                      <Input id="co-colonia" data-testid="checkout-colonia-input" value={form.colonia} onChange={set("colonia")} className="mt-1 border-sand" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="co-ciudad">Ciudad</Label>
                        <Input id="co-ciudad" data-testid="checkout-city-input" value={form.ciudad} onChange={set("ciudad")} className="mt-1 border-sand" />
                      </div>
                      <div>
                        <Label htmlFor="co-cp">C.P.</Label>
                        <Input id="co-cp" data-testid="checkout-cp-input" value={form.cp} onChange={set("cp")} className="mt-1 border-sand" placeholder="97000" />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="co-ref">Referencias (opcional)</Label>
                      <Input id="co-ref" data-testid="checkout-referencias-input" value={form.referencias} onChange={set("referencias")} className="mt-1 border-sand" placeholder="Portón negro, junto al parque..." />
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 rounded-xl bg-surface p-4 text-sm text-clay" data-testid="pickup-info">
                    Recoge tu pedido en <strong className="text-cocoa">{PICKUP_ADDRESS}</strong>. Te contactaremos
                    por WhatsApp para agendar tu cita cuando esté listo.
                  </div>
                )}

                <div className="mt-4">
                  <Label htmlFor="co-notes">Notas para tu pedido (opcional)</Label>
                  <Textarea id="co-notes" data-testid="checkout-notes-input" value={form.notes} onChange={set("notes")} className="mt-1 border-sand" placeholder="Alergias, instrucciones especiales..." />
                </div>
              </div>

              <Button data-testid="checkout-continue-button" onClick={goToPayment} className="w-full rounded-full bg-habanero py-6 text-base font-bold text-white hover:bg-habanero-dark sm:w-auto sm:px-12">
                Continuar al pago <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
          ) : (
            <div className="space-y-6" data-testid="checkout-step-payment">
              <div className="rounded-2xl border border-sand bg-white p-6">
                <h2 className="font-script text-xl font-semibold text-cocoa">Pago seguro con Stripe</h2>
                <p className="mt-2 text-sm text-clay">
                  Al confirmar, te llevaremos a la pasarela segura de Stripe para pagar con tarjeta de crédito o débito.
                  Tu pedido queda registrado al instante.
                </p>
                <div className="mt-4 flex items-center gap-2 rounded-xl bg-surface p-4 text-sm text-cocoa">
                  <CreditCard size={18} className="text-habanero" />
                  Visa · Mastercard · AMEX · Tarjetas mexicanas
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" data-testid="checkout-back-button" onClick={() => setStep(1)} className="rounded-full border-sand px-8 text-cocoa">
                  <ArrowLeft className="mr-2" size={16} /> Volver
                </Button>
                <Button data-testid="checkout-pay-button" onClick={pay} disabled={loading} className="rounded-full bg-habanero px-10 py-6 text-base font-bold text-white hover:bg-habanero-dark disabled:opacity-60">
                  {loading ? "Preparando pago..." : `Pagar ${money(total)} con Stripe`}
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        <aside className="h-fit rounded-2xl border border-sand bg-white p-6 lg:sticky lg:top-24" data-testid="checkout-summary">
          <h2 className="font-script text-xl font-semibold text-cocoa">Resumen del pedido</h2>
          <div className="mt-4 space-y-3">
            {items.map((i) => (
              <div key={`${i.slug}-${i.variant_id}`} className="flex items-center gap-3">
                <img src={i.image} alt={i.name} className="h-12 w-12 rounded-lg border border-sand object-cover" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-cocoa">{i.name}</p>
                  <p className="text-xs text-clay">{i.variant_label} × {i.qty}</p>
                </div>
                <span className="text-sm font-bold text-cocoa">{money(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <Separator className="my-4 bg-sand" />
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-clay">
              <span>Subtotal</span><span data-testid="summary-subtotal">{money(subtotal)}</span>
            </div>
            <div className="flex justify-between text-clay">
              <span>Envío</span>
              <span data-testid="summary-shipping">{shipping === 0 ? "Pick up (gratis)" : money(shipping)}</span>
            </div>
            <div className="flex justify-between pt-2 font-display text-xl font-bold text-cocoa">
              <span>Total</span><span data-testid="summary-total">{money(total)}</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
